package com.ijoin.ui.util

import androidx.compose.ui.graphics.Color
import kotlin.math.pow

/**
 * Extracts button color from HTML landing page.
 * Looks for background-color in:
 * 1. Inline styles on button elements
 * 2. CSS rules in <style> tags
 */
/**
 * Result of button color extraction with contrast text color.
 */
data class ButtonColors(
    val background: Color,
    val text: Color
)

object HtmlButtonColorExtractor {

    /**
     * Calculate relative luminance of a color (0.0 = black, 1.0 = white).
     * Uses sRGB luminance formula.
     */
    fun luminance(color: Color): Float {
        fun linearize(channel: Float): Float {
            return if (channel <= 0.03928f) {
                channel / 12.92f
            } else {
                ((channel + 0.055f) / 1.055f).toDouble().pow(2.4).toFloat()
            }
        }
        val r = linearize(color.red)
        val g = linearize(color.green)
        val b = linearize(color.blue)
        return 0.2126f * r + 0.7152f * g + 0.0722f * b
    }

    /**
     * Returns contrasting text color (black or white) for given background.
     */
    fun contrastingTextColor(background: Color): Color {
        return if (luminance(background) > 0.5f) Color.Black else Color.White
    }

    /**
     * Extract button colors (background + contrasting text) from HTML.
     * Returns null if no button color found.
     */
    fun extractButtonColors(html: String?): ButtonColors? {
        val bg = extractButtonColor(html) ?: return null
        return ButtonColors(
            background = bg,
            text = contrastingTextColor(bg)
        )
    }

    // Regex to extract class attribute from button-like elements
    private val BUTTON_CLASS_REGEX = Regex(
        """<(?:button|a)[^>]*\bclass\s*=\s*["']([^"']+)["'][^>]*>""",
        RegexOption.IGNORE_CASE
    )

    private val BUTTON_INLINE_STYLE_REGEX = Regex(
        """<(?:button|a|input)[^>]*\bstyle\s*=\s*["']([^"']+)["'][^>]*>""",
        RegexOption.IGNORE_CASE
    )

    private val CSS_BUTTON_RULE_REGEX = Regex(
        """(?:button|\.btn|\.button|input\[type=["']?submit["']?\])\s*\{([^}]+)\}""",
        RegexOption.IGNORE_CASE
    )

    private val BACKGROUND_COLOR_REGEX = Regex(
        """background(?:-color)?\s*:\s*([^;]+)""",
        RegexOption.IGNORE_CASE
    )

    // Tailwind bg-{color}-{shade} class pattern
    private val TAILWIND_BG_CLASS_REGEX = Regex("""bg-([a-z]+)-(\d{2,3})""")

    // CSS variable pattern: var(--color-brand-600) or var(--brand-600)
    private val CSS_VAR_REGEX = Regex("""var\s*\(\s*--([^)]+)\s*\)""")

    // CSS variable definition: --color-brand-600: #xxx or --brand-600: #xxx
    private val CSS_VAR_DEF_REGEX = Regex("""--([a-z0-9-]+)\s*:\s*([^;]+)""", RegexOption.IGNORE_CASE)

    private val HEX_COLOR_REGEX = Regex("""#([0-9a-fA-F]{3,8})""")
    private val RGB_COLOR_REGEX = Regex("""rgba?\s*\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)(?:\s*,\s*([0-9.]+))?\s*\)""")
    private val HSL_COLOR_REGEX = Regex("""hsla?\s*\(\s*(\d+)\s*,\s*(\d+)%?\s*,\s*(\d+)%?(?:\s*,\s*([0-9.]+))?\s*\)""")

    /**
     * Extract button color from HTML content.
     * Returns null if no button or no color found.
     *
     * Supports:
     * - Inline styles (style="background-color: #xxx")
     * - CSS rules in <style> tags
     * - Tailwind CSS classes (bg-blue-600, bg-brand-500, etc.)
     * - CSS variables (var(--brand-600))
     */
    fun extractButtonColor(html: String?): Color? {
        if (html.isNullOrBlank()) return null

        val styleContent = extractStyleContent(html)
        val cssVariables = extractCssVariables(styleContent)

        // First try Tailwind classes on button-like elements
        BUTTON_CLASS_REGEX.findAll(html).forEach { match ->
            val classes = match.groupValues[1]
            val color = extractTailwindBgColor(classes, styleContent, cssVariables)
            if (color != null) return color
        }

        // Then try inline styles on buttons
        BUTTON_INLINE_STYLE_REGEX.findAll(html).forEach { match ->
            val style = match.groupValues[1]
            val color = extractColorFromStyle(style, cssVariables)
            if (color != null) return color
        }

        // Then try CSS rules in <style> tags
        CSS_BUTTON_RULE_REGEX.findAll(styleContent).forEach { match ->
            val cssBlock = match.groupValues[1]
            val color = extractColorFromStyle(cssBlock, cssVariables)
            if (color != null) return color
        }

        return null
    }

    /**
     * Extract CSS variables from style content.
     */
    private fun extractCssVariables(styleContent: String): Map<String, String> {
        val vars = mutableMapOf<String, String>()
        CSS_VAR_DEF_REGEX.findAll(styleContent).forEach { match ->
            val name = match.groupValues[1].trim()
            val value = match.groupValues[2].trim()
            vars[name] = value
        }
        return vars
    }

    /**
     * Extract color from Tailwind bg-{color}-{shade} class.
     * First tries to find custom color definition in CSS, then falls back to standard Tailwind colors.
     */
    private fun extractTailwindBgColor(
        classes: String,
        styleContent: String,
        cssVariables: Map<String, String>
    ): Color? {
        val match = TAILWIND_BG_CLASS_REGEX.find(classes) ?: return null
        val colorName = match.groupValues[1]
        val shade = match.groupValues[2]

        // Try to find custom color in CSS variables (e.g., --color-brand-600 or --brand-600)
        val possibleVarNames = listOf(
            "color-$colorName-$shade",
            "$colorName-$shade"
        )
        for (varName in possibleVarNames) {
            cssVariables[varName]?.let { value ->
                parseColor(value)?.let { return it }
            }
        }

        // Try to find .bg-{color}-{shade} class definition in CSS
        val classDef = Regex("""\\.bg-$colorName-$shade\s*\{([^}]+)\}""").find(styleContent)
        if (classDef != null) {
            extractColorFromStyle(classDef.groupValues[1], cssVariables)?.let { return it }
        }

        // Fallback to standard Tailwind colors
        return tailwindColor(colorName, shade.toIntOrNull() ?: 500)
    }

    private fun extractStyleContent(html: String): String {
        val styleRegex = Regex("""<style[^>]*>([\s\S]*?)</style>""", RegexOption.IGNORE_CASE)
        return styleRegex.findAll(html)
            .map { it.groupValues[1] }
            .joinToString("\n")
    }

    private fun extractColorFromStyle(style: String, cssVariables: Map<String, String> = emptyMap()): Color? {
        val match = BACKGROUND_COLOR_REGEX.find(style) ?: return null
        var colorValue = match.groupValues[1].trim()

        // Resolve CSS variable if present
        CSS_VAR_REGEX.find(colorValue)?.let { varMatch ->
            val varName = varMatch.groupValues[1].trim()
            cssVariables[varName]?.let { resolved ->
                colorValue = resolved
            }
        }

        return parseColor(colorValue)
    }

    /**
     * Standard Tailwind CSS color palette.
     */
    private fun tailwindColor(name: String, shade: Int): Color? {
        val colors = TAILWIND_COLORS[name] ?: return null
        return colors[shade]
    }

    private val TAILWIND_COLORS: Map<String, Map<Int, Color>> = mapOf(
        "slate" to mapOf(
            50 to Color(0xFFF8FAFC), 100 to Color(0xFFF1F5F9), 200 to Color(0xFFE2E8F0),
            300 to Color(0xFFCBD5E1), 400 to Color(0xFF94A3B8), 500 to Color(0xFF64748B),
            600 to Color(0xFF475569), 700 to Color(0xFF334155), 800 to Color(0xFF1E293B), 900 to Color(0xFF0F172A)
        ),
        "gray" to mapOf(
            50 to Color(0xFFF9FAFB), 100 to Color(0xFFF3F4F6), 200 to Color(0xFFE5E7EB),
            300 to Color(0xFFD1D5DB), 400 to Color(0xFF9CA3AF), 500 to Color(0xFF6B7280),
            600 to Color(0xFF4B5563), 700 to Color(0xFF374151), 800 to Color(0xFF1F2937), 900 to Color(0xFF111827)
        ),
        "zinc" to mapOf(
            50 to Color(0xFFFAFAFA), 100 to Color(0xFFF4F4F5), 200 to Color(0xFFE4E4E7),
            300 to Color(0xFFD4D4D8), 400 to Color(0xFFA1A1AA), 500 to Color(0xFF71717A),
            600 to Color(0xFF52525B), 700 to Color(0xFF3F3F46), 800 to Color(0xFF27272A), 900 to Color(0xFF18181B)
        ),
        "red" to mapOf(
            50 to Color(0xFFFEF2F2), 100 to Color(0xFFFEE2E2), 200 to Color(0xFFFECACA),
            300 to Color(0xFFFCA5A5), 400 to Color(0xFFF87171), 500 to Color(0xFFEF4444),
            600 to Color(0xFFDC2626), 700 to Color(0xFFB91C1C), 800 to Color(0xFF991B1B), 900 to Color(0xFF7F1D1D)
        ),
        "orange" to mapOf(
            50 to Color(0xFFFFF7ED), 100 to Color(0xFFFFEDD5), 200 to Color(0xFFFED7AA),
            300 to Color(0xFFFDBA74), 400 to Color(0xFFFB923C), 500 to Color(0xFFF97316),
            600 to Color(0xFFEA580C), 700 to Color(0xFFC2410C), 800 to Color(0xFF9A3412), 900 to Color(0xFF7C2D12)
        ),
        "amber" to mapOf(
            50 to Color(0xFFFFFBEB), 100 to Color(0xFFFEF3C7), 200 to Color(0xFFFDE68A),
            300 to Color(0xFFFCD34D), 400 to Color(0xFFFBBF24), 500 to Color(0xFFF59E0B),
            600 to Color(0xFFD97706), 700 to Color(0xFFB45309), 800 to Color(0xFF92400E), 900 to Color(0xFF78350F)
        ),
        "yellow" to mapOf(
            50 to Color(0xFFFEFCE8), 100 to Color(0xFFFEF9C3), 200 to Color(0xFFFEF08A),
            300 to Color(0xFFFDE047), 400 to Color(0xFFFACC15), 500 to Color(0xFFEAB308),
            600 to Color(0xFFCA8A04), 700 to Color(0xFFA16207), 800 to Color(0xFF854D0E), 900 to Color(0xFF713F12)
        ),
        "lime" to mapOf(
            50 to Color(0xFFF7FEE7), 100 to Color(0xFFECFCCB), 200 to Color(0xFFD9F99D),
            300 to Color(0xFFBEF264), 400 to Color(0xFFA3E635), 500 to Color(0xFF84CC16),
            600 to Color(0xFF65A30D), 700 to Color(0xFF4D7C0F), 800 to Color(0xFF3F6212), 900 to Color(0xFF365314)
        ),
        "green" to mapOf(
            50 to Color(0xFFF0FDF4), 100 to Color(0xFFDCFCE7), 200 to Color(0xFFBBF7D0),
            300 to Color(0xFF86EFAC), 400 to Color(0xFF4ADE80), 500 to Color(0xFF22C55E),
            600 to Color(0xFF16A34A), 700 to Color(0xFF15803D), 800 to Color(0xFF166534), 900 to Color(0xFF14532D)
        ),
        "emerald" to mapOf(
            50 to Color(0xFFECFDF5), 100 to Color(0xFFD1FAE5), 200 to Color(0xFFA7F3D0),
            300 to Color(0xFF6EE7B7), 400 to Color(0xFF34D399), 500 to Color(0xFF10B981),
            600 to Color(0xFF059669), 700 to Color(0xFF047857), 800 to Color(0xFF065F46), 900 to Color(0xFF064E3B)
        ),
        "teal" to mapOf(
            50 to Color(0xFFF0FDFA), 100 to Color(0xFFCCFBF1), 200 to Color(0xFF99F6E4),
            300 to Color(0xFF5EEAD4), 400 to Color(0xFF2DD4BF), 500 to Color(0xFF14B8A6),
            600 to Color(0xFF0D9488), 700 to Color(0xFF0F766E), 800 to Color(0xFF115E59), 900 to Color(0xFF134E4A)
        ),
        "cyan" to mapOf(
            50 to Color(0xFFECFEFF), 100 to Color(0xFFCFFAFE), 200 to Color(0xFFA5F3FC),
            300 to Color(0xFF67E8F9), 400 to Color(0xFF22D3EE), 500 to Color(0xFF06B6D4),
            600 to Color(0xFF0891B2), 700 to Color(0xFF0E7490), 800 to Color(0xFF155E75), 900 to Color(0xFF164E63)
        ),
        "sky" to mapOf(
            50 to Color(0xFFF0F9FF), 100 to Color(0xFFE0F2FE), 200 to Color(0xFFBAE6FD),
            300 to Color(0xFF7DD3FC), 400 to Color(0xFF38BDF8), 500 to Color(0xFF0EA5E9),
            600 to Color(0xFF0284C7), 700 to Color(0xFF0369A1), 800 to Color(0xFF075985), 900 to Color(0xFF0C4A6E)
        ),
        "blue" to mapOf(
            50 to Color(0xFFEFF6FF), 100 to Color(0xFFDBEAFE), 200 to Color(0xFFBFDBFE),
            300 to Color(0xFF93C5FD), 400 to Color(0xFF60A5FA), 500 to Color(0xFF3B82F6),
            600 to Color(0xFF2563EB), 700 to Color(0xFF1D4ED8), 800 to Color(0xFF1E40AF), 900 to Color(0xFF1E3A8A)
        ),
        "indigo" to mapOf(
            50 to Color(0xFFEEF2FF), 100 to Color(0xFFE0E7FF), 200 to Color(0xFFC7D2FE),
            300 to Color(0xFFA5B4FC), 400 to Color(0xFF818CF8), 500 to Color(0xFF6366F1),
            600 to Color(0xFF4F46E5), 700 to Color(0xFF4338CA), 800 to Color(0xFF3730A3), 900 to Color(0xFF312E81)
        ),
        "violet" to mapOf(
            50 to Color(0xFFF5F3FF), 100 to Color(0xFFEDE9FE), 200 to Color(0xFFDDD6FE),
            300 to Color(0xFFC4B5FD), 400 to Color(0xFFA78BFA), 500 to Color(0xFF8B5CF6),
            600 to Color(0xFF7C3AED), 700 to Color(0xFF6D28D9), 800 to Color(0xFF5B21B6), 900 to Color(0xFF4C1D95)
        ),
        "purple" to mapOf(
            50 to Color(0xFFFAF5FF), 100 to Color(0xFFF3E8FF), 200 to Color(0xFFE9D5FF),
            300 to Color(0xFFD8B4FE), 400 to Color(0xFFC084FC), 500 to Color(0xFFA855F7),
            600 to Color(0xFF9333EA), 700 to Color(0xFF7E22CE), 800 to Color(0xFF6B21A8), 900 to Color(0xFF581C87)
        ),
        "fuchsia" to mapOf(
            50 to Color(0xFFFDF4FF), 100 to Color(0xFFFAE8FF), 200 to Color(0xFFF5D0FE),
            300 to Color(0xFFF0ABFC), 400 to Color(0xFFE879F9), 500 to Color(0xFFD946EF),
            600 to Color(0xFFC026D3), 700 to Color(0xFFA21CAF), 800 to Color(0xFF86198F), 900 to Color(0xFF701A75)
        ),
        "pink" to mapOf(
            50 to Color(0xFFFDF2F8), 100 to Color(0xFFFCE7F3), 200 to Color(0xFFFBCFE8),
            300 to Color(0xFFF9A8D4), 400 to Color(0xFFF472B6), 500 to Color(0xFFEC4899),
            600 to Color(0xFFDB2777), 700 to Color(0xFFBE185D), 800 to Color(0xFF9D174D), 900 to Color(0xFF831843)
        ),
        "rose" to mapOf(
            50 to Color(0xFFFFF1F2), 100 to Color(0xFFFFE4E6), 200 to Color(0xFFFECDD3),
            300 to Color(0xFFFDA4AF), 400 to Color(0xFFFB7185), 500 to Color(0xFFF43F5E),
            600 to Color(0xFFE11D48), 700 to Color(0xFFBE123C), 800 to Color(0xFF9F1239), 900 to Color(0xFF881337)
        ),
        // Custom brand colors - common defaults, will be overridden by CSS variables if present
        "brand" to mapOf(
            50 to Color(0xFFEFF6FF), 100 to Color(0xFFDBEAFE), 200 to Color(0xFFBFDBFE),
            300 to Color(0xFF93C5FD), 400 to Color(0xFF60A5FA), 500 to Color(0xFF3B82F6),
            600 to Color(0xFF2563EB), 700 to Color(0xFF1D4ED8), 800 to Color(0xFF1E40AF), 900 to Color(0xFF1E3A8A)
        ),
        "primary" to mapOf(
            50 to Color(0xFFEEF2FF), 100 to Color(0xFFE0E7FF), 200 to Color(0xFFC7D2FE),
            300 to Color(0xFFA5B4FC), 400 to Color(0xFF818CF8), 500 to Color(0xFF6366F1),
            600 to Color(0xFF4F46E5), 700 to Color(0xFF4338CA), 800 to Color(0xFF3730A3), 900 to Color(0xFF312E81)
        )
    )

    private fun parseColor(colorValue: String): Color? {
        // Try hex color
        HEX_COLOR_REGEX.find(colorValue)?.let { match ->
            return parseHexColor(match.groupValues[1])
        }

        // Try rgb/rgba
        RGB_COLOR_REGEX.find(colorValue)?.let { match ->
            val r = match.groupValues[1].toIntOrNull() ?: return null
            val g = match.groupValues[2].toIntOrNull() ?: return null
            val b = match.groupValues[3].toIntOrNull() ?: return null
            val a = match.groupValues.getOrNull(4)?.toFloatOrNull() ?: 1f
            return Color(r, g, b, (a * 255).toInt())
        }

        // Try hsl/hsla
        HSL_COLOR_REGEX.find(colorValue)?.let { match ->
            val h = match.groupValues[1].toIntOrNull() ?: return null
            val s = match.groupValues[2].toIntOrNull() ?: return null
            val l = match.groupValues[3].toIntOrNull() ?: return null
            val a = match.groupValues.getOrNull(4)?.toFloatOrNull() ?: 1f
            return hslToColor(h, s, l, a)
        }

        // Try named colors
        return namedColorToColor(colorValue.lowercase().trim())
    }

    private fun parseHexColor(hex: String): Color? {
        return when (hex.length) {
            3 -> {
                val r = hex[0].toString().repeat(2).toIntOrNull(16) ?: return null
                val g = hex[1].toString().repeat(2).toIntOrNull(16) ?: return null
                val b = hex[2].toString().repeat(2).toIntOrNull(16) ?: return null
                Color(r, g, b)
            }
            4 -> {
                val r = hex[0].toString().repeat(2).toIntOrNull(16) ?: return null
                val g = hex[1].toString().repeat(2).toIntOrNull(16) ?: return null
                val b = hex[2].toString().repeat(2).toIntOrNull(16) ?: return null
                val a = hex[3].toString().repeat(2).toIntOrNull(16) ?: return null
                Color(r, g, b, a)
            }
            6 -> {
                val r = hex.substring(0, 2).toIntOrNull(16) ?: return null
                val g = hex.substring(2, 4).toIntOrNull(16) ?: return null
                val b = hex.substring(4, 6).toIntOrNull(16) ?: return null
                Color(r, g, b)
            }
            8 -> {
                val r = hex.substring(0, 2).toIntOrNull(16) ?: return null
                val g = hex.substring(2, 4).toIntOrNull(16) ?: return null
                val b = hex.substring(4, 6).toIntOrNull(16) ?: return null
                val a = hex.substring(6, 8).toIntOrNull(16) ?: return null
                Color(r, g, b, a)
            }
            else -> null
        }
    }

    private fun hslToColor(h: Int, s: Int, l: Int, alpha: Float): Color {
        val hNorm = h / 360f
        val sNorm = s / 100f
        val lNorm = l / 100f

        val c = (1 - kotlin.math.abs(2 * lNorm - 1)) * sNorm
        val x = c * (1 - kotlin.math.abs((hNorm * 6) % 2 - 1))
        val m = lNorm - c / 2

        val (r1, g1, b1) = when {
            hNorm < 1f / 6 -> Triple(c, x, 0f)
            hNorm < 2f / 6 -> Triple(x, c, 0f)
            hNorm < 3f / 6 -> Triple(0f, c, x)
            hNorm < 4f / 6 -> Triple(0f, x, c)
            hNorm < 5f / 6 -> Triple(x, 0f, c)
            else -> Triple(c, 0f, x)
        }

        return Color(
            red = r1 + m,
            green = g1 + m,
            blue = b1 + m,
            alpha = alpha
        )
    }

    private fun namedColorToColor(name: String): Color? = when (name) {
        "black" -> Color.Black
        "white" -> Color.White
        "red" -> Color.Red
        "green" -> Color.Green
        "blue" -> Color.Blue
        "yellow" -> Color.Yellow
        "cyan" -> Color.Cyan
        "magenta" -> Color.Magenta
        "gray", "grey" -> Color.Gray
        "darkgray", "darkgrey" -> Color.DarkGray
        "lightgray", "lightgrey" -> Color.LightGray
        "orange" -> Color(0xFFFFA500)
        "purple" -> Color(0xFF800080)
        "pink" -> Color(0xFFFFC0CB)
        "brown" -> Color(0xFFA52A2A)
        "navy" -> Color(0xFF000080)
        "teal" -> Color(0xFF008080)
        "olive" -> Color(0xFF808000)
        "maroon" -> Color(0xFF800000)
        "lime" -> Color(0xFF00FF00)
        "aqua" -> Color(0xFF00FFFF)
        "fuchsia" -> Color(0xFFFF00FF)
        "silver" -> Color(0xFFC0C0C0)
        "coral" -> Color(0xFFFF7F50)
        "tomato" -> Color(0xFFFF6347)
        "gold" -> Color(0xFFFFD700)
        "indigo" -> Color(0xFF4B0082)
        "violet" -> Color(0xFFEE82EE)
        "crimson" -> Color(0xFFDC143C)
        else -> null
    }
}
